<template>
  <NavBar pageName="SIM Cards"></NavBar>

  <!-- Buy SIM Dialog -->
  <BuyNewSIMCardService
    v-if="showBuySimDialog"
    @newClientServiceAddedEvent="onSimPurchased"
    @dialogClosedEvent="closeBuySimDialog"
  />

  <!-- Good Order Dialog -->
  <GoodOrderDialog
    v-model="showGoodOrderDialog"
    :initial-category="'imsi'"
    @orderCreated="onGoodOrderCreated"
  />

  <!-- IMSI Details Popup -->
  <ImsiDetailsPopup
    v-if="showDetailsPopup"
    :sim-id="selectedSimId"
    :imsi-id="selectedImsiId"
    @close="closeDetailsPopup"
    @updated="onDetailsUpdated"
  />

  <!-- Add Subscription Dialog -->
  <AddSubscriptionDialog
    v-if="showAddSubscriptionDialog"
    :sim-id="selectedSimForSubscription.sim_id"
    :sim-details="selectedSimForSubscription"
    @close="closeAddSubscriptionDialog"
    @subscriptionAdded="onSubscriptionAdded"
  />

  <!-- Move IMSI Dialog -->
  <MoveImsiDialog
    v-if="showMoveImsiDialog"
    :sim-id="selectedSimForMove.sim_id"
    :sim-details="selectedSimForMove"
    @close="closeMoveImsiDialog"
    @moved="onImsiMoved"
  />

  <!-- Action Confirmation Dialog -->
  <v-dialog v-model="showActionDialog" max-width="400">
    <v-card>
      <v-card-title>{{ actionDialogTitle }}</v-card-title>
      <v-card-text>{{ actionDialogMessage }}</v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="showActionDialog = false">Cancel</v-btn>
        <v-btn
          :color="actionDialogColor"
          :loading="actionLoading"
          @click="executeAction"
        >
          {{ actionDialogConfirmText }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Provision Dialog -->
  <v-dialog v-model="showProvisionDialog" max-width="600">
    <v-card>
      <v-card-title class="bg-primary text-white">
        {{ isReprovision ? 'Reprovision SIM' : 'Provision SIM' }}
      </v-card-title>
      <v-card-text class="pt-4">
        <div v-if="provisionItem" class="mb-4">
          <strong>SIM ID:</strong> {{ provisionItem.sim_id }}<br>
          <strong>Serial:</strong> {{ provisionItem.serial || '-' }}<br>
          <strong>IMSI:</strong> {{ provisionItem.imsi || '-' }}<br>
          <strong>Client:</strong> {{ provisionItem.client_name || '-' }}
        </div>

        <!-- Reprovision info alert -->
        <v-alert v-if="isReprovision && checkingMsisdnAvailability" type="info" variant="tonal" class="mb-4">
          <v-progress-circular indeterminate size="20" class="mr-2"></v-progress-circular>
          Checking if old MSISDN {{ oldMsisdn }} is available...
        </v-alert>

        <v-alert v-if="isReprovision && !checkingMsisdnAvailability && oldMsisdnAvailable" type="success" variant="tonal" class="mb-4">
          <v-icon class="mr-2">mdi-check-circle</v-icon>
          Old MSISDN <strong>{{ oldMsisdn }}</strong> is available and pre-selected.
        </v-alert>

        <v-alert v-if="isReprovision && !checkingMsisdnAvailability && !oldMsisdnAvailable && oldMsisdn" type="warning" variant="tonal" class="mb-4">
          <v-icon class="mr-2">mdi-alert</v-icon>
          Old MSISDN <strong>{{ oldMsisdn }}</strong> is no longer available. Please select a new MSISDN.
        </v-alert>

        <!-- MSISDN Selection Section -->
        <template v-if="needsMsisdnSelection">
          <div class="text-subtitle-1 font-weight-bold mb-2">1. Select MSISDN</div>

          <!-- Show search only if old MSISDN is not available or we need to search -->
          <template v-if="!oldMsisdnAvailable || !isReprovision">
            <v-row class="mb-2">
              <v-col cols="12" md="5">
                <v-text-field
                    v-model="msisdnLike"
                    label="MSISDN pattern"
                    placeholder="e.g. 0788"
                    density="compact"
                    clearable
                    @keyup.enter="searchMsisdnsForProvision"
                    @click="$event.target.select()"
                    @dblclick="msisdnLike = ''"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="5">
                <v-select
                    v-model="selectedMsisdnClassId"
                    :items="msisdnClasses"
                    item-title="name"
                    item-value="id"
                    label="MSISDN Class"
                    density="compact"
                    @update:model-value="searchMsisdnsForProvision"
                ></v-select>
              </v-col>
              <v-col cols="12" md="2" class="d-flex align-center">
                <v-btn color="primary" size="small" @click="searchMsisdnsForProvision" :loading="msisdnSearchLoading">
                  Search
                </v-btn>
              </v-col>
            </v-row>
          </template>

          <v-autocomplete
              v-model="selectedMsisdnForProvision"
              :items="availableMsisdns"
              item-title="msisdn"
              item-value="msisdn_id"
              label="Select MSISDN"
              :hint="oldMsisdnAvailable ? 'Old MSISDN is pre-selected' : 'Choose an available MSISDN for this SIM'"
              persistent-hint
              :loading="msisdnSearchLoading || checkingMsisdnAvailability"
              :disabled="checkingMsisdnAvailability"
              class="mb-4"
          ></v-autocomplete>

          <!-- Button to search for different MSISDN if old one was pre-selected -->
          <v-btn
            v-if="isReprovision && oldMsisdnAvailable"
            variant="text"
            size="small"
            color="primary"
            @click="loadMsisdnClassesForProvision"
            class="mb-4"
          >
            <v-icon size="small" class="mr-1">mdi-magnify</v-icon>
            Search for different MSISDN
          </v-btn>
        </template>

        <!-- MSISDN auto-assign message -->
        <template v-else-if="!provisionItem?.msisdn_id">
          <v-alert type="info" variant="tonal" class="mb-4">
            An MSISDN will be automatically assigned from the standard class.
          </v-alert>
        </template>

        <!-- Product Code Selection Section -->
        <div class="text-subtitle-1 font-weight-bold mb-2">{{ needsMsisdnSelection ? '2.' : '1.' }} Select Product Code</div>
        <v-select
            v-model="selectedProductForProvision"
            :items="productCodesWithDescription"
            item-title="display"
            item-value="name"
            label="Select Product Code"
            hint="Choose the MNO product code for provisioning"
            persistent-hint
            :loading="productsLoading"
            class="mb-4"
            :rules="[v => !!v || 'Product code is required']"
        ></v-select>

      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="grey" @click="showProvisionDialog = false">Cancel</v-btn>
        <v-btn
            color="primary"
            @click="executeProvisionFromDialog"
            :loading="provisionLoading"
            :disabled="!selectedProductForProvision || (needsMsisdnSelection && !selectedMsisdnForProvision)"
        >
          Provision
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Action Result Snackbar -->
  <!-- Add to Inventory Dialog -->
  <v-dialog v-model="showInventoryDialog" max-width="550">
    <v-card>
      <v-card-title class="bg-teal text-white">
        Add to Inventory
      </v-card-title>
      <v-card-text class="pt-4">
        <v-alert type="info" density="compact" class="mb-4">
          <div><strong>SIM ID:</strong> {{ inventoryItem?.sim_id }}</div>
          <div><strong>IMSI:</strong> {{ inventoryItem?.imsi }}</div>
          <div><strong>Serial:</strong> {{ inventoryItem?.serial || '-' }}</div>
        </v-alert>

        <v-select
          v-model="selectedInventoryClientId"
          :items="clientsHierarchyList.filter(c => c.id !== null)"
          item-title="displayName"
          item-value="id"
          label="Client *"
          variant="outlined"
          density="compact"
          class="mb-3"
          :rules="[v => !!v || 'Client is required']"
        ></v-select>

        <v-text-field
          v-model="inventoryLocation"
          label="Location"
          variant="outlined"
          density="compact"
          class="mb-3"
          placeholder="e.g. Warehouse A, Store 1"
          @click="$event.target.select()"
          @dblclick="inventoryLocation = ''"
        ></v-text-field>

        <v-select
          v-model="selectedInventoryStatusId"
          :items="inventoryStatuses"
          item-title="name"
          item-value="id"
          label="Inventory Status *"
          variant="outlined"
          density="compact"
          class="mb-3"
          :rules="[v => !!v || 'Status is required']"
        ></v-select>

        <v-textarea
          v-model="inventoryNotes"
          label="Notes"
          variant="outlined"
          density="compact"
          rows="2"
        ></v-textarea>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="showInventoryDialog = false">Cancel</v-btn>
        <v-btn
          color="teal"
          variant="flat"
          @click="addToInventory"
          :loading="inventoryLoading"
          :disabled="!selectedInventoryStatusId || !selectedInventoryClientId"
        >
          Add to Inventory
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Bulk Add to Inventory Dialog -->
  <v-dialog v-model="showBulkInventoryDialog" max-width="550">
    <v-card>
      <v-card-title class="bg-teal text-white">
        Bulk Add to Inventory
      </v-card-title>
      <v-card-text class="pt-4">
        <v-alert type="info" density="compact" class="mb-4">
          <div><strong>{{ bulkInventoryEligibleCount }}</strong> SIMs can be added to inventory</div>
          <div v-if="bulkInventorySkipCount > 0" class="text-caption">
            {{ bulkInventorySkipCount }} will be skipped (already in inventory or no SIM)
          </div>
        </v-alert>

        <v-select
          v-model="selectedBulkInventoryClientId"
          :items="clientsHierarchyList.filter(c => c.id !== null)"
          item-title="displayName"
          item-value="id"
          label="Client *"
          variant="outlined"
          density="compact"
          class="mb-3"
          :rules="[v => !!v || 'Client is required']"
        ></v-select>

        <v-text-field
          v-model="bulkInventoryLocation"
          label="Location"
          variant="outlined"
          density="compact"
          class="mb-3"
          placeholder="e.g. Warehouse A, Store 1"
          @click="$event.target.select()"
          @dblclick="bulkInventoryLocation = ''"
        ></v-text-field>

        <v-select
          v-model="selectedBulkInventoryStatusId"
          :items="inventoryStatuses"
          item-title="name"
          item-value="id"
          label="Inventory Status *"
          variant="outlined"
          density="compact"
          class="mb-3"
          :rules="[v => !!v || 'Status is required']"
        ></v-select>

        <v-textarea
          v-model="bulkInventoryNotes"
          label="Notes"
          variant="outlined"
          density="compact"
          rows="2"
        ></v-textarea>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="showBulkInventoryDialog = false">Cancel</v-btn>
        <v-btn
          color="teal"
          variant="flat"
          @click="executeBulkAddToInventory"
          :loading="bulkInventoryLoading"
          :disabled="!selectedBulkInventoryStatusId || !selectedBulkInventoryClientId || bulkInventoryEligibleCount === 0"
        >
          Add {{ bulkInventoryEligibleCount }} to Inventory
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Change Inventory Dialog -->
  <v-dialog v-model="showChangeStatusDialog" max-width="550">
    <v-card>
      <v-card-title class="bg-teal text-white">
        Edit Inventory
      </v-card-title>
      <v-card-text class="pt-4">
        <v-alert type="info" density="compact" class="mb-4">
          <div><strong>SIM ID:</strong> {{ changeStatusItem?.sim_id }}</div>
          <div><strong>IMSI:</strong> {{ changeStatusItem?.imsi }}</div>
          <div><strong>Serial:</strong> {{ changeStatusItem?.serial || '-' }}</div>
        </v-alert>

        <v-select
          v-model="editInventoryClientId"
          :items="clientsHierarchyList.filter(c => c.id !== null)"
          item-title="displayName"
          item-value="id"
          label="Client *"
          variant="outlined"
          density="compact"
          class="mb-3"
          :rules="[v => !!v || 'Client is required']"
        ></v-select>

        <v-text-field
          v-model="editInventoryLocation"
          label="Location"
          variant="outlined"
          density="compact"
          class="mb-3"
          placeholder="e.g. Warehouse A, Store 1"
          @click="$event.target.select()"
          @dblclick="editInventoryLocation = ''"
        ></v-text-field>

        <v-select
          v-model="newInventoryStatusId"
          :items="inventoryStatuses"
          item-title="name"
          item-value="id"
          label="Status *"
          variant="outlined"
          density="compact"
          class="mb-3"
          :rules="[v => !!v || 'Status is required']"
        ></v-select>

        <v-textarea
          v-model="editInventoryNotes"
          label="Notes"
          variant="outlined"
          density="compact"
          rows="2"
        ></v-textarea>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="showChangeStatusDialog = false">Cancel</v-btn>
        <v-btn
          color="teal"
          variant="flat"
          @click="executeChangeInventoryStatus"
          :loading="changeStatusLoading"
          :disabled="!newInventoryStatusId || !editInventoryClientId"
        >
          Update
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Remove from Inventory Confirmation Dialog -->
  <v-dialog v-model="showRemoveInventoryDialog" max-width="450">
    <v-card>
      <v-card-title class="bg-error text-white">
        <v-icon class="mr-2">mdi-delete</v-icon>
        Remove from Inventory
      </v-card-title>
      <v-card-text class="pt-4">
        <div class="mb-4">
          <div><strong>SIM ID:</strong> {{ removeInventoryItem?.sim_id }}</div>
          <div><strong>IMSI:</strong> {{ removeInventoryItem?.imsi }}</div>
        </div>
        <v-alert type="warning" density="compact">
          This will remove the SIM from inventory tracking. The inventory history will be preserved.
        </v-alert>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="showRemoveInventoryDialog = false">Cancel</v-btn>
        <v-btn
          color="error"
          variant="flat"
          @click="executeRemoveFromInventory"
          :loading="removeInventoryLoading"
        >
          Remove
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-snackbar v-model="showSnackbar" :color="snackbarColor" :timeout="4000">
    {{ snackbarMessage }}
  </v-snackbar>

  <!-- Bulk Action Confirmation Dialog -->
  <v-dialog v-model="showBulkActionDialog" max-width="450">
    <v-card>
      <v-card-title>Bulk {{ bulkActionType }}</v-card-title>
      <v-card-text>
        Are you sure you want to <strong>{{ bulkActionType }}</strong> {{ selectAllMode ? totalResults : selectedItems.length }} SIM cards?
        <div v-if="selectAllMode" class="text-caption mt-2 text-warning">
          This will affect ALL {{ totalResults }} filtered results.
        </div>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="showBulkActionDialog = false">Cancel</v-btn>
        <v-btn color="primary" :loading="bulkActionLoading" @click="executeBulkAction">
          Confirm
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Create Group Dialog -->
  <v-dialog v-model="showCreateGroupDialog" max-width="500">
    <v-card>
      <v-card-title>
        <v-icon class="mr-2">mdi-folder-plus</v-icon>
        Create IMSI Group
      </v-card-title>
      <v-card-text>
        <ClientSearchFilter
          v-model="newGroupClientId"
          :show-include-subclients="false"
          client-select-label="Client proprietar *"
          @hierarchy-loaded="onCreateGroupHierarchyLoaded"
        />
        <v-text-field
          v-model="newGroupName"
          label="Group Name"
          required
          density="compact"
          class="mb-3"
          @click="$event.target.select()"
          @dblclick="newGroupName = ''"
        ></v-text-field>
        <v-textarea
          v-model="newGroupDescription"
          label="Description (optional)"
          rows="2"
          density="compact"
        ></v-textarea>
        <div class="text-caption mt-2">
          {{ selectAllMode ? totalResults : selectedItems.length }} SIM cards will be added to this group.
        </div>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="showCreateGroupDialog = false">Cancel</v-btn>
        <v-btn
          color="primary"
          :loading="createGroupLoading"
          :disabled="!newGroupName || !newGroupClientId"
          @click="createGroup"
        >
          Create Group
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Add to Group Dialog -->
  <v-dialog v-model="showAddToGroupDialog" max-width="550">
    <v-card>
      <v-card-title>
        <v-icon class="mr-2">mdi-folder-arrow-right</v-icon>
        Add to Group
      </v-card-title>
      <v-card-text>
        <!-- Select existing group or create new -->
        <template v-if="!showInlineCreateGroup">
          <v-select
            v-model="selectedGroupId"
            :items="existingGroups"
            item-title="name"
            item-value="id"
            label="Select Group"
            density="compact"
            :loading="loadingExistingGroups"
          >
            <template v-slot:item="{ item, props }">
              <v-list-item v-bind="props">
                <template v-slot:append>
                  <v-chip size="x-small">{{ item.raw.member_count }} members</v-chip>
                </template>
              </v-list-item>
            </template>
            <template v-slot:no-data>
              <v-list-item>
                <v-list-item-title class="text-grey">No groups found</v-list-item-title>
              </v-list-item>
            </template>
          </v-select>

          <v-btn
            variant="text"
            color="primary"
            size="small"
            class="mt-2"
            @click="showInlineCreateGroup = true"
          >
            <v-icon size="small" class="mr-1">mdi-plus</v-icon>
            Create new group
          </v-btn>
        </template>

        <!-- Inline create group form -->
        <template v-else>
          <v-alert type="info" density="compact" variant="tonal" class="mb-3">
            Create a new group, then add the selected IMSIs to it.
          </v-alert>
          <v-text-field
            v-model="inlineGroupName"
            label="Group Name"
            required
            density="compact"
            class="mb-2"
            :error-messages="inlineGroupError"
            @click="$event.target.select()"
            @dblclick="inlineGroupName = ''"
          ></v-text-field>
          <v-textarea
            v-model="inlineGroupDescription"
            label="Description (optional)"
            rows="2"
            density="compact"
            class="mb-2"
          ></v-textarea>
          <v-autocomplete
            v-model="inlineGroupClientId"
            v-model:search="inlineGroupClientSearch"
            :items="clientsHierarchyList.filter(c => c.id !== null)"
            item-title="displayName"
            item-value="id"
            label="Owner Client"
            density="compact"
            hint="Client that owns this group"
            persistent-hint
            clearable
            @click="onInlineClientFieldClick"
            @focus="onInlineClientFieldClick"
          ></v-autocomplete>

          <div class="d-flex gap-2 mt-3">
            <v-btn
              variant="text"
              size="small"
              @click="cancelInlineCreateGroup"
            >
              Back to select
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn
              color="success"
              size="small"
              :loading="inlineCreateGroupLoading"
              :disabled="!inlineGroupName"
              @click="createGroupInline"
            >
              Create & Select
            </v-btn>
          </div>
        </template>

        <v-divider class="my-3" v-if="!showInlineCreateGroup"></v-divider>
        <div class="text-caption" v-if="!showInlineCreateGroup">
          {{ selectAllMode ? totalResults : selectedItems.length }} SIM cards will be added to the selected group.
        </div>
      </v-card-text>
      <v-card-actions v-if="!showInlineCreateGroup">
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="closeAddToGroupDialog">Cancel</v-btn>
        <v-btn
          color="primary"
          :loading="addToGroupLoading"
          :disabled="!selectedGroupId"
          @click="addToGroup"
        >
          Add to Group
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Bulk Move Dialog -->
  <v-dialog v-model="showBulkMoveDialog" max-width="500">
    <v-card>
      <v-card-title>
        <v-icon class="mr-2">mdi-account-arrow-right</v-icon>
        Move to Subclient
      </v-card-title>
      <v-card-text>
        <v-autocomplete
          v-model="selectedTargetClient"
          :items="clientsHierarchyList.filter(c => c.id !== null)"
          item-title="displayName"
          item-value="id"
          label="Select Target Client"
          density="compact"
        ></v-autocomplete>
        <div class="text-caption mt-2">
          {{ selectAllMode ? totalResults : selectedItems.length }} SIM cards will be moved to the selected client.
        </div>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="showBulkMoveDialog = false">Cancel</v-btn>
        <v-btn
          color="primary"
          :loading="bulkMoveLoading"
          :disabled="!selectedTargetClient"
          @click="executeBulkMove"
        >
          Move
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-container style="margin-top: 185px;">
    <!-- Header with Buy SIM button -->
    <v-row class="mb-4">
      <v-col cols="12" class="d-flex justify-space-between align-center">
        <div class="d-flex align-center">
          <h2>IMSI Management</h2>
          <v-tooltip text="Rebuild client hierarchy cache" location="bottom">
            <template v-slot:activator="{ props }">
              <v-btn v-bind="props" icon="mdi-database-refresh" variant="text" size="small" class="ml-2" :loading="cacheRebuilding" @click="rebuildCache" />
            </template>
          </v-tooltip>
        </div>
        <div class="d-flex ga-2">
          <v-btn
            variant="outlined"
            color="secondary"
            @click="$router.push('/imsiInventory')"
          >
            <v-icon start>mdi-database-plus</v-icon>
            IMSI Inventory
          </v-btn>
          <v-btn
            v-if="!isDistributor"
            color="success"
            @click="showGoodOrderDialog = true"
          >
            <v-icon start>mdi-cart-plus</v-icon>
            Comanda SIM-uri
          </v-btn>
          <v-btn color="primary" @click="openBuySimDialog">
            Buy SIM Card
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <!-- Filters Card -->
    <ImsiFilters
      :filters="filters"
      :networks="networks"
      :imsi-groups="imsiGroups"
      :clients-hierarchy="clientsHierarchy"
      :has-subclients="hasSubclients"
      :include-subclients="includeSubclientsToggle"
      :loading="loading"
      :has-results="imsisList.length > 0"
      @update:filters="filters = $event"
      @update:include-subclients="includeSubclientsToggle = $event"
      @search="loadImsis"
      @reset="resetFilters"
      @export="exportCSV"
      @client-change="onClientChange"
    />

    <!-- Search Hint (when no search done yet) -->
    <v-alert v-if="!initialLoadDone && !loading" type="info" variant="tonal" class="mb-4">
      <v-icon class="mr-2">mdi-information</v-icon>
      Introduceti minim 3 caractere in campul de cautare, selectati un client, un grup, o retea, sau alegeti un status specific pentru a afisa rezultate.
      Puteti folosi <strong>*</strong> ca wildcard (ex: <strong>123*456</strong>).
    </v-alert>

    <!-- Results Summary -->
    <v-row v-if="initialLoadDone" class="mb-2">
      <v-col cols="12">
        <span class="text-caption">
          Showing {{ imsisList.length }} of {{ totalResults }} results
        </span>
      </v-col>
    </v-row>

    <!-- Selection Toolbar (Sticky) -->
    <ImsiToolbar
      v-if="initialLoadDone"
      :selected-count="selectedItems.length"
      :select-all-mode="selectAllMode"
      :total-results="totalResults"
      :current-page-count="imsisList.length"
      :loading-all-ids="loadingAllIds"
      :has-subclients="hasSubclients"
      @select-all="selectAllFiltered"
      @clear-selection="clearSelection"
      @create-group="openCreateGroupDialog"
      @add-to-group="openAddToGroupDialog"
      @bulk-action="confirmBulkAction"
      @bulk-move="openBulkMoveDialog"
      @bulk-inventory="openBulkInventoryDialog"
    />

    <!-- Results Table -->
    <ImsiDataTable
      v-if="initialLoadDone || loading"
      :items="imsisList"
      :selected-items="selectedItems"
      :loading="loading"
      :per-page="perPage"
      :sort-by="sortBy"
      :refreshing-sim-id="refreshingSimId"
      :move-label="t.imsi?.move || 'Muta'"
      @update:selected-items="selectedItems = $event"
      @update:sort-by="sortBy = $event; onSortChange()"
      @open-details="openDetailsPopup"
      @navigate-to-msisdn="navigateToMsisdnList"
      @navigate-to-services="navigateToSimServices"
      @action="confirmAction"
      @add-subscription="openAddSubscriptionDialog"
      @reprovision="reprovisionTerminatedImsi"
      @refresh="refreshSimStatus"
      @move="openMoveImsiDialog"
      @inventory-add="openInventoryDialog"
      @inventory-view="goToInventory"
      @inventory-change="openChangeInventoryStatusDialog"
      @inventory-remove="removeFromInventory"
    />

    <!-- Pagination -->
    <v-row class="mt-4" v-if="totalPages > 1">
      <v-col cols="12" class="d-flex justify-center">
        <v-pagination
          v-model="currentPage"
          :length="totalPages"
          @update:model-value="onPageChange"
        ></v-pagination>
      </v-col>
    </v-row>

  </v-container>
</template>

<script>
import { defineComponent } from 'vue';
import useGetLoggedIn from "@/composables/useGetLoggedIn"
import {log} from "@/composables/useLog";
import {getDisplaySettings} from "@/composables/useDisplaySettingsAPI";
import { t as tRef } from "@/composables/useLanguages";
import NavBar from "@/components/NavBar.vue";
import store from "@/store";
import BuyNewSIMCardService from "@/components/BuyNewSIMCardService.vue";
import ImsiDetailsPopup from "@/components/ImsiDetailsPopup.vue";
import AddSubscriptionDialog from "@/components/AddSubscriptionDialog.vue";
import MoveImsiDialog from "@/components/MoveImsiDialog.vue";
import GoodOrderDialog from "@/components/GoodOrderDialog.vue";
import ClientSearchFilter from "@/components/ClientSearchFilter.vue";
// Subcomponents
import ImsiFilters from "@/components/imsis/ImsiFilters.vue";
import ImsiToolbar from "@/components/imsis/ImsiToolbar.vue";
import ImsiDataTable from "@/components/imsis/ImsiDataTable.vue";
import { getAllocatedImsis, suspendSim, reactivateSim, terminateSim, provisionSim, reprovisionTerminatedImsi } from "@/composables/useGetAllocatedImsisAPI";
import { getMsisdnClasses, getAvailableMsisdnsList, checkMsisdnAvailability } from "@/composables/useGetAvailableMsisdnsAPI";
import { refreshSim } from "@/composables/useGetSimsAPI";
import { getProductCodesForProvisioning } from "@/composables/useProductsAPI";
import { getImsiGroups, createImsiGroup, addImsisToGroup, getAllFilteredSimIds, bulkSuspendSims, bulkReactivateSims, bulkTerminateSims, bulkProvisionSims, bulkMoveImsisToSubclient } from "@/composables/useGroupsAPI";
import { addSimsToInventory, getInventoryStatuses, updateGoodsInventoryStatus, deleteGoodsInventory } from "@/composables/useGoodsInventoryAPI";

export default defineComponent({
  name: 'ImsisList',
  components: {
    NavBar,
    BuyNewSIMCardService,
    ImsiDetailsPopup,
    AddSubscriptionDialog,
    MoveImsiDialog,
    GoodOrderDialog,
    ImsiFilters,
    ImsiToolbar,
    ImsiDataTable,
    ClientSearchFilter
  },
  data() {
    return {
      loading: false,
      token: '',
      client_id: store.getters.getUser?.client_id,
      isDistributor: false,
      isMultilevelReseller: false,

      // Dialogs
      showBuySimDialog: false,
      showGoodOrderDialog: false,
      showDetailsPopup: false,
      selectedSimId: null,
      selectedImsiId: null,
      showAddSubscriptionDialog: false,
      selectedSimForSubscription: {},
      showMoveImsiDialog: false,
      selectedSimForMove: {},

      // Action dialog
      showActionDialog: false,
      actionDialogTitle: '',
      actionDialogMessage: '',
      actionDialogConfirmText: '',
      actionDialogColor: 'primary',
      actionLoading: false,
      pendingAction: null,
      pendingItem: null,

      // Snackbar
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',

      // Inventory dialog (add)
      showInventoryDialog: false,
      inventoryLoading: false,
      inventoryItem: null,
      inventoryStatuses: [],
      selectedInventoryStatusId: null,
      selectedInventoryClientId: null,
      inventoryLocation: '',
      inventoryNotes: '',

      // Bulk inventory dialog
      showBulkInventoryDialog: false,
      bulkInventoryLoading: false,
      selectedBulkInventoryStatusId: null,
      selectedBulkInventoryClientId: null,
      bulkInventoryLocation: '',
      bulkInventoryNotes: '',

      // Change/edit inventory dialog
      showChangeStatusDialog: false,
      changeStatusLoading: false,
      changeStatusItem: null,
      newInventoryStatusId: null,
      editInventoryClientId: null,
      editInventoryLocation: '',
      editInventoryNotes: '',

      // Remove from inventory dialog
      showRemoveInventoryDialog: false,
      removeInventoryLoading: false,
      removeInventoryItem: null,

      // Search control - don't load all records on page open
      initialLoadDone: false,

      // Filters
      filters: {
        statusMode: 'all',
        search_term: '',
        client_id: null,
        imsi_group_id: null,
        network_id: null
      },

      // IMSI Groups for filtering
      imsiGroups: [],

      // Networks for filtering
      networks: [],

      // Client hierarchy
      clientsHierarchy: null,
      hasSubclients: false,
      includeSubclientsToggle: false,

      // Pagination
      currentPage: 1,
      perPage: 20,
      totalResults: 0,

      // Data
      imsisList: [],

      // Sorting
      sortBy: [],

      // Selection
      selectedItems: [],
      selectAllMode: false,
      allFilteredImsiIds: [],
      allFilteredSimIds: [],
      loadingAllIds: false,

      // Bulk action dialog
      showBulkActionDialog: false,
      bulkActionType: null,
      bulkActionLoading: false,

      // Create group dialog
      showCreateGroupDialog: false,
      newGroupName: '',
      newGroupDescription: '',
      newGroupClientId: null,
      createGroupLoading: false,

      // Add to group dialog
      showAddToGroupDialog: false,
      existingGroups: [],
      selectedGroupId: null,
      addToGroupLoading: false,
      loadingExistingGroups: false,

      // Inline create group (inside Add to Group dialog)
      showInlineCreateGroup: false,
      inlineGroupName: '',
      inlineGroupDescription: '',
      inlineGroupClientId: null,
      inlineGroupClientSearch: '',
      inlineGroupError: '',
      inlineCreateGroupLoading: false,

      // Move dialog
      showBulkMoveDialog: false,
      selectedTargetClient: null,
      bulkMoveLoading: false,

      // Refresh SIM
      refreshingSimId: null,

      // Provision dialog
      showProvisionDialog: false,
      provisionLoading: false,
      provisionItem: null,
      selectedMsisdnForProvision: null,
      selectedMsisdnClassId: null,
      msisdnLike: '',
      msisdnClasses: [],
      availableMsisdns: [],
      msisdnSearchLoading: false,
      selectedProductForProvision: null,
      availableProducts: [],
      productsLoading: false,
      hasSelectMsisdnRole: false,
      needsMsisdnSelection: false,

      // Cache rebuild
      cacheRebuilding: false,

      // Reprovision terminated IMSI
      isReprovision: false,
      oldMsisdnAvailable: false,
      oldMsisdnId: null,
      oldMsisdn: null,
      checkingMsisdnAvailability: false
    }
  },
  computed: {
    t() {
      return tRef.value;
    },
    totalPages() {
      return Math.ceil(this.totalResults / this.perPage)
    },
    clientsHierarchyList() {
      // Build list with "All" option and hierarchical indentation based on level
      const list = [{id: null, displayName: 'All Clients', name: 'All Clients'}];

      this.clientsHierarchy.forEach(client => {
        const indent = client.level > 0 ? '—'.repeat(client.level) + ' ' : '';
        list.push({
          id: client.id,
          displayName: indent + client.name,
          name: client.name,
          level: client.level,
          has_subclients: client.has_subclients
        });
      });

      return list;
    },
    imsiGroupsWithAll() {
      return [
        { id: null, name: 'All Groups' },
        ...this.imsiGroups
      ];
    },
    bulkInventoryEligibleCount() {
      // Count items that have a SIM and are not already in inventory
      return this.selectedItems.filter(item => item.sim_id && !item.in_inventory).length
    },
    bulkInventorySkipCount() {
      // Count items that will be skipped (no SIM or already in inventory)
      return this.selectedItems.filter(item => !item.sim_id || item.in_inventory).length
    },
    productCodesWithDescription() {
      return this.availableProducts.map(pc => ({
        ...pc,
        display: pc.description ? `${pc.name} - ${pc.description}` : pc.name
      }))
    },
    hasSearchCriteria() {
      if (!this.isMultilevelReseller) return true
      const search = this.filters.search_term ? this.filters.search_term.trim().replace(/\*/g, '') : ''
      return (search.length >= 3) ||
             (this.filters.client_id !== null) ||
             (this.filters.imsi_group_id !== null) ||
             (this.filters.network_id !== null) ||
             (!['all'].includes(this.filters.statusMode))
    }
  },
  methods: {
    async loadImsis() {
      if (!this.hasSearchCriteria) {
        this.imsisList = []
        this.totalResults = 0
        this.initialLoadDone = false
        return
      }
      log("Loading IMSIs with filters")
      this.loading = true
      this.initialLoadDone = true

      try {
        // Convert statusMode to statuses array
        let statuses = []
        let statusAll = false
        if (this.filters.statusMode === 'all') {
          statusAll = true
        } else {
          statuses = [this.filters.statusMode]
        }

        // Get sort parameters
        let sortField = null
        let sortDirection = 'asc'
        if (this.sortBy && this.sortBy.length > 0) {
          sortField = this.sortBy[0].key
          sortDirection = this.sortBy[0].order || 'asc'
        }

        const options = {
          statuses: statuses,
          status_all: statusAll,
          search_term: this.filters.search_term ? this.filters.search_term.trim() : '',
          client_id: this.filters.client_id || (!this.isMultilevelReseller ? this.client_id : null),
          include_subclients: this.includeSubclientsToggle,
          imsi_group_id: this.filters.imsi_group_id,
          network_id: this.filters.network_id,
          sort_by: sortField,
          sort_direction: sortDirection,
          page: this.currentPage,
          per_page: this.perPage
        }

        const response = await getAllocatedImsis(this.token, options)

        if (!response.error) {
          // Ensure each item has a unique identifier
          const items = response.imsi_list || []
          this.imsisList = items.map((item, index) => ({
            ...item,
            _uniqueId: item.imsi_id || `temp_${index}`
          }))
          this.totalResults = response.total_count || 0
          // Update hierarchical clients list from response
          if (response.filters) {
            // Always update clients hierarchy to ensure it's available
            this.clientsHierarchy = response.filters.clients_hierarchy || []
            this.hasSubclients = response.filters.has_subclients || false
            this.imsiGroups = response.filters.imsi_groups || []
            // Load networks only once
            if (this.networks.length === 0) {
              this.networks = response.filters.networks || []
            }
          }
          log("Loaded " + this.imsisList.length + " IMSIs")
          // Debug: Log first item to see is_distributor and is_available_for_move fields
          if (this.imsisList.length > 0) {
            console.log("First IMSI item fields:", JSON.stringify(this.imsisList[0], null, 2));
          }
        } else {
          log("Error loading IMSIs: " + response.message)
          this.showSnackbarMessage(response.message || 'Error loading SIM cards', 'error')
        }
      } catch (err) {
        log("Exception loading IMSIs:")
        log(err)
        this.showSnackbarMessage('Error loading SIM cards', 'error')
      } finally {
        this.loading = false
      }
    },

    resetFilters() {
      this.filters = {
        statusMode: 'all',
        search_term: '',
        client_id: null,
        imsi_group_id: null,
        network_id: null
      }
      this.includeSubclientsToggle = false
      this.sortBy = []
      this.currentPage = 1
      this.imsiGroups = []
      this.imsisList = []
      this.totalResults = 0
      this.initialLoadDone = false
    },

    onClientChange() {
      // Reset group filter when client changes
      this.filters.imsi_group_id = null
      // Groups will be reloaded with the next loadImsis call
    },

    onInlineClientFieldClick() {
      this.$nextTick(() => {
        this.inlineGroupClientSearch = '';
      });
    },

    onPageChange(page) {
      this.currentPage = page
      this.loadImsis()
    },

    onSortChange() {
      this.currentPage = 1
      this.loadImsis()
    },

    openBuySimDialog() {
      this.showBuySimDialog = true
    },

    closeBuySimDialog() {
      this.showBuySimDialog = false
    },

    onSimPurchased(data) {
      log("SIM purchased:")
      log(data)
      this.showBuySimDialog = false
      this.showSnackbarMessage('SIM card purchased successfully', 'success')
      this.loadImsis()
    },

    onGoodOrderCreated(response) {
      log("Goods order created:")
      log(response)
      this.showGoodOrderDialog = false
      if (response.payment_url) {
        // Prepaid - redirect to payment
        this.showSnackbarMessage('Redirectionare catre plata...', 'info')
      } else {
        // Postpaid - order placed directly
        this.showSnackbarMessage(response.message || 'Comanda a fost plasata cu succes!', 'success')
      }
    },

    openDetailsPopup(simId, imsiId) {
      this.selectedSimId = simId
      this.selectedImsiId = imsiId
      this.showDetailsPopup = true
    },

    closeDetailsPopup() {
      this.showDetailsPopup = false
      this.selectedSimId = null
      this.selectedImsiId = null
    },

    onDetailsUpdated() {
      this.loadImsis()
    },

    // Check if a SIM can have a subscription added
    // Must be: service_id=22 (allocate_imsi), active, and provisioned (has imsi_id + msisdn_id)
    canAddSubscription(item) {
      return item.service_id === 22 &&
             item.sim_status === 'active' &&
             item.imsi_id &&
             item.msisdn_id
    },

    openAddSubscriptionDialog(item) {
      this.selectedSimForSubscription = {
        sim_id: item.sim_id,
        imsi: item.imsi,
        serial: item.serial,
        msisdn: item.msisdn,
        client_name: item.client_name
      }
      this.showAddSubscriptionDialog = true
    },

    closeAddSubscriptionDialog() {
      this.showAddSubscriptionDialog = false
      this.selectedSimForSubscription = {}
    },

    onSubscriptionAdded(data) {
      log("Subscription added:")
      log(data)
      this.closeAddSubscriptionDialog()
      this.showSnackbarMessage('Subscription added successfully', 'success')
      this.loadImsis()
    },

    // Move IMSI methods
    canMoveImsi(item) {
      // Show Move button only if:
      // 1. IMSI's owning client has is_distributor=true
      // 2. IMSI is available (not in active CS of type imsi_msisdn or imsi_subscription)
      // Using truthy checks instead of strict equality to handle 0/1, "true"/"false", or true/false
      const isDistributor = item.is_distributor === true || item.is_distributor === 1 || item.is_distributor === "true";
      const isAvailable = item.is_available_for_move === true || item.is_available_for_move === 1 || item.is_available_for_move === "true";
      const result = isDistributor && isAvailable;
      console.log("canMoveImsi for sim_id=" + item.sim_id + ": is_distributor=" + item.is_distributor + " (" + typeof item.is_distributor + "), is_available_for_move=" + item.is_available_for_move + " (" + typeof item.is_available_for_move + "), result=" + result);
      return result;
    },

    isPendingSim(item) {
      // Check if SIM status is pending (pending_active, pending, etc.)
      const status = item.sim_status?.toLowerCase() || '';
      return status.includes('pending') && item.sim_id;
    },

    async refreshSimStatus(item) {
      if (!item.sim_id) return;
      this.refreshingSimId = item.sim_id;
      try {
        const response = await refreshSim(this.token, item.sim_id);
        if (!response.error) {
          this.showSnackbarMessage(response.message || 'SIM status refreshed', 'success');
          this.loadImsis();
        } else {
          this.showSnackbarMessage(response.message || 'Error refreshing SIM status', 'error');
        }
      } catch (err) {
        log("Error refreshing SIM status:", err);
        this.showSnackbarMessage('Error refreshing SIM status', 'error');
      } finally {
        this.refreshingSimId = null;
      }
    },

    openMoveImsiDialog(item) {
      this.selectedSimForMove = {
        sim_id: item.sim_id,
        imsi_id: item.imsi_id,
        imsi: item.imsi,
        serial: item.serial,
        client_id: item.client_id,
        client_name: item.client_name,
        is_allocated: item.is_allocated,
        imsi_type_name: item.imsi_type_name
      }
      this.showMoveImsiDialog = true
    },

    closeMoveImsiDialog() {
      this.showMoveImsiDialog = false
      this.selectedSimForMove = {}
    },

    onImsiMoved(data) {
      log("IMSI moved:")
      log(data)
      this.closeMoveImsiDialog()
      this.showSnackbarMessage(data.message || 'IMSI moved successfully', 'success')
      this.loadImsis()
    },

    confirmAction(action, item) {
      this.pendingAction = action
      this.pendingItem = item

      switch (action) {
        case 'suspend':
          this.actionDialogTitle = 'Suspend SIM'
          this.actionDialogMessage = `Are you sure you want to suspend the SIM with IMSI ${item.imsi}?`
          this.actionDialogConfirmText = 'Suspend'
          this.actionDialogColor = 'warning'
          break
        case 'reactivate':
          this.actionDialogTitle = 'Reactivate SIM'
          this.actionDialogMessage = `Are you sure you want to reactivate the SIM with IMSI ${item.imsi}?`
          this.actionDialogConfirmText = 'Reactivate'
          this.actionDialogColor = 'success'
          break
        case 'terminate':
          this.actionDialogTitle = 'Terminate SIM'
          this.actionDialogMessage = `Are you sure you want to terminate the SIM with IMSI ${item.imsi}? This action cannot be undone.`
          this.actionDialogConfirmText = 'Terminate'
          this.actionDialogColor = 'error'
          break
        case 'provision':
          // Open provision dialog instead of generic action dialog
          this.openProvisionDialog(item)
          return
      }

      this.showActionDialog = true
    },

    async executeAction() {
      if (!this.pendingAction || !this.pendingItem) return

      this.actionLoading = true
      let response

      try {
        switch (this.pendingAction) {
          case 'suspend':
            response = await suspendSim(this.token, this.pendingItem.sim_id)
            break
          case 'reactivate':
            response = await reactivateSim(this.token, this.pendingItem.sim_id)
            break
          case 'terminate':
            response = await terminateSim(this.token, this.pendingItem.sim_id)
            break
          // Note: 'provision' case is now handled by openProvisionDialog
        }

        if (response && !response.error) {
          this.showSnackbarMessage(response.message || 'Action completed successfully', 'success')
          this.loadImsis()
        } else {
          this.showSnackbarMessage(response?.message || 'Action failed', 'error')
        }
      } catch (err) {
        log("Error executing action:")
        log(err)
        this.showSnackbarMessage('Error executing action', 'error')
      } finally {
        this.actionLoading = false
        this.showActionDialog = false
        this.pendingAction = null
        this.pendingItem = null
      }
    },

    // Provision dialog methods
    async openProvisionDialog(item) {
      this.provisionItem = item
      this.selectedMsisdnForProvision = null
      this.selectedMsisdnClassId = null
      this.selectedProductForProvision = null
      this.msisdnLike = ''
      this.availableMsisdns = []
      this.availableProducts = []
      this.isReprovision = false
      this.oldMsisdnAvailable = false
      this.oldMsisdnId = null
      this.oldMsisdn = null

      // Always allow MSISDN selection for IMSI provisioning
      this.needsMsisdnSelection = true

      this.showProvisionDialog = true

      // Load products and MSISDN classes in parallel
      this.loadProductsForProvision()
      this.loadMsisdnClassesForProvision()
    },

    async reprovisionTerminatedImsi(item) {
      // For terminated IMSIs, check if old MSISDN is still available
      this.provisionItem = item
      this.selectedMsisdnForProvision = null
      this.selectedMsisdnClassId = null
      this.selectedProductForProvision = null
      this.msisdnLike = ''
      this.availableMsisdns = []
      this.availableProducts = []
      this.isReprovision = true
      this.oldMsisdnAvailable = false
      this.oldMsisdnId = item.msisdn_id || null
      this.oldMsisdn = item.msisdn || null
      this.needsMsisdnSelection = true

      this.showProvisionDialog = true
      this.checkingMsisdnAvailability = true

      // Load products
      this.loadProductsForProvision()

      // Check if old MSISDN is available
      if (this.oldMsisdnId) {
        try {
          const response = await checkMsisdnAvailability(this.token, this.oldMsisdnId)
          if (response && response.available) {
            this.oldMsisdnAvailable = true
            // Pre-add the old MSISDN to the list and select it
            this.availableMsisdns = [{
              msisdn_id: this.oldMsisdnId,
              msisdn: this.oldMsisdn
            }]
            this.selectedMsisdnForProvision = this.oldMsisdnId
            this.showSnackbarMessage(`Old MSISDN ${this.oldMsisdn} is available`, 'success')
          } else {
            // MSISDN not available, user must select a new one
            this.oldMsisdnAvailable = false
            this.showSnackbarMessage(`Old MSISDN ${this.oldMsisdn} is not available. Please select a new one.`, 'warning')
            await this.loadMsisdnClassesForProvision()
          }
        } catch (err) {
          log('Error checking MSISDN availability:', err)
          this.oldMsisdnAvailable = false
          await this.loadMsisdnClassesForProvision()
        }
      } else {
        // No old MSISDN, load classes for selection
        await this.loadMsisdnClassesForProvision()
      }

      this.checkingMsisdnAvailability = false
    },

    async loadMsisdnClassesForProvision() {
      try {
        const classesResult = await getMsisdnClasses(this.token)
        if (!classesResult.error) {
          this.msisdnClasses = classesResult.msisdns_classes || []
          // Set first class as default
          if (this.msisdnClasses.length > 0) {
            this.selectedMsisdnClassId = this.msisdnClasses[0].id
          }
        }
      } catch (err) {
        this.showSnackbarMessage('Error loading MSISDN classes', 'error')
      }
      await this.searchMsisdnsForProvision()
    },

    async loadProductsForProvision() {
      this.productsLoading = true
      try {
        const result = await getProductCodesForProvisioning(this.token)
        if (!result.error) {
          this.availableProducts = result.product_codes || []
        } else {
          this.showSnackbarMessage('Error loading product codes', 'error')
        }
      } catch (err) {
        log('Error loading product codes:', err)
        this.showSnackbarMessage('Error loading product codes', 'error')
      } finally {
        this.productsLoading = false
      }
    },

    async searchMsisdnsForProvision() {
      this.msisdnSearchLoading = true
      try {
        const options = {
          msisdn_class_id: this.selectedMsisdnClassId,
          limit: 100
        }
        if (this.msisdnLike && this.msisdnLike.trim()) {
          options.msisdn_like = this.msisdnLike.trim()
        }
        // Pass imsi_id to filter MSISDNs by same network
        if (this.provisionItem?.imsi_id) {
          options.imsi_id = this.provisionItem.imsi_id
        }

        const result = await getAvailableMsisdnsList(this.token, options)
        if (!result.error) {
          this.availableMsisdns = result.msisdns_list || []
        } else {
          this.showSnackbarMessage(result.message, 'error')
        }
      } catch (err) {
        this.showSnackbarMessage('Error searching MSISDNs', 'error')
      } finally {
        this.msisdnSearchLoading = false
      }
    },

    async executeProvisionFromDialog() {
      if (!this.provisionItem) return

      // Validate Product Code selection (always required)
      if (!this.selectedProductForProvision) {
        this.showSnackbarMessage('Please select a Product Code', 'warning')
        return
      }

      // Validate MSISDN selection if needed
      if (this.needsMsisdnSelection && !this.selectedMsisdnForProvision) {
        this.showSnackbarMessage('Please select an MSISDN', 'warning')
        return
      }

      this.provisionLoading = true
      try {
        const msisdnId = this.needsMsisdnSelection ? this.selectedMsisdnForProvision : null
        const mnoProduct = this.selectedProductForProvision

        let result
        if (this.isReprovision) {
          // For terminated SIMs, use the reprovision endpoint which creates a new SIM
          result = await reprovisionTerminatedImsi(this.token, this.provisionItem.imsi_id, msisdnId, mnoProduct)
        } else {
          // For not_provisioned SIMs, use the regular provision endpoint
          result = await provisionSim(this.token, this.provisionItem.sim_id, msisdnId, null, mnoProduct, this.provisionItem.client_service_id)
        }

        if (result.error) {
          this.showSnackbarMessage(result.message || 'Provisioning failed', 'error')
        } else {
          this.showSnackbarMessage(result.message || 'SIM provisioning started', 'success')
          this.showProvisionDialog = false
          this.provisionItem = null
          // Refresh after a short delay
          setTimeout(() => this.loadImsis(), 1000)
        }
      } catch (err) {
        log('Error in executeProvisionFromDialog:', err)
        this.showSnackbarMessage('Error during provisioning', 'error')
      } finally {
        this.provisionLoading = false
      }
    },

    // Inventory methods
    async openInventoryDialog(item) {
      this.inventoryItem = item
      this.selectedInventoryStatusId = null
      this.selectedInventoryClientId = this.client_id // Default to current user's client
      this.inventoryLocation = ''
      this.inventoryNotes = ''

      // Load inventory statuses if not already loaded
      if (this.inventoryStatuses.length === 0) {
        const result = await getInventoryStatuses(this.token)
        if (!result.error) {
          this.inventoryStatuses = result.inventory_statuses || []
        }
      }

      // Ensure clients hierarchy is loaded
      if (this.clientsHierarchy.length === 0) {
        await this.loadImsis()
      }

      this.showInventoryDialog = true
    },

    async addToInventory() {
      if (!this.inventoryItem || !this.selectedInventoryStatusId || !this.selectedInventoryClientId) return

      this.inventoryLoading = true
      try {
        const result = await addSimsToInventory(
          this.token,
          [this.inventoryItem.sim_id],
          this.selectedInventoryStatusId,
          this.selectedInventoryClientId,
          this.inventoryLocation,
          this.inventoryNotes
        )

        if (!result.error) {
          this.showSnackbarMessage(result.message || 'Added to inventory', 'success')
          this.showInventoryDialog = false
          // Reload the list to update the inventory status
          await this.loadImsis()
        } else {
          this.showSnackbarMessage(result.message || 'Error adding to inventory', 'error')
        }
      } catch (err) {
        log('Error adding to inventory:', err)
        this.showSnackbarMessage('Error adding to inventory', 'error')
      } finally {
        this.inventoryLoading = false
      }
    },

    goToInventory(item) {
      // Navigate to inventory page with filter for this SIM
      this.$router.push({
        name: 'goodsInventory',
        query: { search: item.sim_id }
      })
    },

    async openBulkInventoryDialog() {
      if (this.selectedItems.length === 0) {
        this.showSnackbarMessage('No items selected', 'warning')
        return
      }

      if (this.bulkInventoryEligibleCount === 0) {
        this.showSnackbarMessage('No eligible SIMs to add to inventory', 'warning')
        return
      }

      this.selectedBulkInventoryStatusId = null
      this.selectedBulkInventoryClientId = this.client_id // Default to current user's client
      this.bulkInventoryLocation = ''
      this.bulkInventoryNotes = ''

      // Load inventory statuses if not already loaded
      if (this.inventoryStatuses.length === 0) {
        const result = await getInventoryStatuses(this.token)
        if (!result.error) {
          this.inventoryStatuses = result.inventory_statuses || []
        }
      }

      // Ensure clients hierarchy is loaded
      if (this.clientsHierarchy.length === 0) {
        await this.loadImsis()
      }

      this.showBulkInventoryDialog = true
    },

    async executeBulkAddToInventory() {
      if (!this.selectedBulkInventoryStatusId || !this.selectedBulkInventoryClientId || this.bulkInventoryEligibleCount === 0) return

      this.bulkInventoryLoading = true
      try {
        // Get only the eligible SIM IDs (have SIM and not in inventory)
        const eligibleSimIds = this.selectedItems
          .filter(item => item.sim_id && !item.in_inventory)
          .map(item => item.sim_id)

        const result = await addSimsToInventory(
          this.token,
          eligibleSimIds,
          this.selectedBulkInventoryStatusId,
          this.selectedBulkInventoryClientId,
          this.bulkInventoryLocation,
          this.bulkInventoryNotes
        )

        if (!result.error) {
          this.showSnackbarMessage(
            `Added ${result.success_count || eligibleSimIds.length} SIMs to inventory`,
            'success'
          )
          this.showBulkInventoryDialog = false
          this.selectedItems = []
          // Reload the list to update inventory status
          this.loadImsis()
        } else {
          this.showSnackbarMessage(result.message || 'Error adding to inventory', 'error')
        }
      } catch (err) {
        log('Error in bulk add to inventory:', err)
        this.showSnackbarMessage('Error adding to inventory', 'error')
      } finally {
        this.bulkInventoryLoading = false
      }
    },

    async openChangeInventoryStatusDialog(item) {
      this.changeStatusItem = item
      // Pre-populate with current values
      this.editInventoryClientId = item.inventory_client_id || this.client_id
      this.editInventoryLocation = item.inventory_location || ''
      this.newInventoryStatusId = null // Let user select new status
      this.editInventoryNotes = ''

      // Load inventory statuses if not already loaded
      if (this.inventoryStatuses.length === 0) {
        const result = await getInventoryStatuses(this.token)
        if (!result.error) {
          this.inventoryStatuses = result.inventory_statuses || []
        }
      }

      // Ensure clients hierarchy is loaded (reload if empty)
      if (this.clientsHierarchy.length === 0) {
        await this.loadImsis()
      }

      // Pre-select current status
      const currentStatus = this.inventoryStatuses.find(s => s.name === item.inventory_status)
      if (currentStatus) {
        this.newInventoryStatusId = currentStatus.id
      }

      this.showChangeStatusDialog = true
    },

    async executeChangeInventoryStatus() {
      if (!this.changeStatusItem || !this.newInventoryStatusId || !this.editInventoryClientId) return

      this.changeStatusLoading = true
      try {
        const result = await updateGoodsInventoryStatus(this.token, {
          id: this.changeStatusItem.inventory_id,
          inventory_status_id: this.newInventoryStatusId,
          client_id: this.editInventoryClientId,
          location: this.editInventoryLocation,
          notes: this.editInventoryNotes
        })

        if (!result.error) {
          this.showSnackbarMessage('Inventory updated', 'success')
          this.showChangeStatusDialog = false
          this.loadImsis()
        } else {
          this.showSnackbarMessage(result.message || 'Error updating inventory', 'error')
        }
      } catch (err) {
        log('Error updating inventory:', err)
        this.showSnackbarMessage('Error updating inventory', 'error')
      } finally {
        this.changeStatusLoading = false
      }
    },

    removeFromInventory(item) {
      this.removeInventoryItem = item
      this.showRemoveInventoryDialog = true
    },

    async executeRemoveFromInventory() {
      if (!this.removeInventoryItem) return

      this.removeInventoryLoading = true
      try {
        const result = await deleteGoodsInventory(this.token, this.removeInventoryItem.inventory_id)

        if (!result.error) {
          this.showSnackbarMessage('Removed from inventory', 'success')
          this.showRemoveInventoryDialog = false
          this.loadImsis()
        } else {
          this.showSnackbarMessage(result.message || 'Error removing from inventory', 'error')
        }
      } catch (err) {
        log('Error removing from inventory:', err)
        this.showSnackbarMessage('Error removing from inventory', 'error')
      } finally {
        this.removeInventoryLoading = false
      }
    },

    showSnackbarMessage(message, color) {
      this.snackbarMessage = message
      this.snackbarColor = color
      this.showSnackbar = true
    },

    getStatusColor(status) {
      const colors = {
        'active': 'success',
        'suspended': 'warning',
        'not_provisioned': 'info',
        'terminated': 'error',
        'not_allocated': 'grey'
      }
      return colors[status] || 'grey'
    },

    formatStatus(status) {
      const labels = {
        'active': 'Active',
        'suspended': 'Suspended',
        'not_provisioned': 'Not Provisioned',
        'terminated': 'Terminated',
        'not_allocated': 'Not Allocated'
      }
      return labels[status] || status
    },

    getRowProps({ item }) {
      if (item.sim_status === 'suspended') {
        return { class: 'row-suspended' }
      }
      if (item.sim_status === 'not_provisioned') {
        return { class: 'row-not_provisioned' }
      }
      return {}
    },

    formatDate(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleDateString()
    },

    isExpiringSoon(dateString) {
      if (!dateString) return false
      const expirationDate = new Date(dateString)
      const now = new Date()
      const daysUntilExpiration = (expirationDate - now) / (1000 * 60 * 60 * 24)
      return daysUntilExpiration <= 7 && daysUntilExpiration > 0
    },

    navigateToMsisdnList(msisdn) {
      if (msisdn) {
        this.$router.push({ name: 'msisdns', query: { search: msisdn } })
      }
    },

    navigateToSimServices(simId) {
      if (simId) {
        this.$router.push({ path: '/clientServices', query: { sim_id: simId } })
      }
    },

    // ==================== Selection Methods ====================

    async selectAllFiltered() {
      this.loadingAllIds = true
      try {
        const filters = this.getCurrentFilters()
        const response = await getAllFilteredSimIds(this.token, filters)
        if (!response.error) {
          this.allFilteredImsiIds = response.imsi_ids || []
          this.allFilteredSimIds = response.sim_ids || []  // For bulk operations
          // With return-object, select all items on current page visually
          // The actual operations will use allFilteredImsiIds/allFilteredSimIds when selectAllMode is true
          this.selectedItems = [...this.imsisList]
          this.selectAllMode = true
        } else {
          this.showSnackbarMessage(response.message || 'Error loading all IDs', 'error')
        }
      } catch (err) {
        log("Error in selectAllFiltered:", err)
        this.showSnackbarMessage('Error loading all IDs', 'error')
      } finally {
        this.loadingAllIds = false
      }
    },

    clearSelection() {
      this.selectedItems = []
      this.selectAllMode = false
      this.allFilteredImsiIds = []
      this.allFilteredSimIds = []
    },

    getCurrentFilters() {
      return {
        status_mode: this.filters.statusMode === 'all' ? null : this.filters.statusMode,
        search_term: this.filters.search_term ? this.filters.search_term.trim() : '',
        client_id: this.filters.client_id,
        include_subclients: this.includeSubclientsToggle,
        network_id: this.filters.network_id,
        imsi_group_id: this.filters.imsi_group_id
      }
    },

    // ==================== Group Methods ====================

    onCreateGroupHierarchyLoaded({ currentClientId }) {
      if (!this.newGroupClientId && currentClientId) {
        this.newGroupClientId = currentClientId
      }
    },

    openCreateGroupDialog() {
      this.newGroupName = ''
      this.newGroupDescription = ''
      this.newGroupClientId = null
      this.showCreateGroupDialog = true
    },

    async createGroup() {
      if (!this.newGroupName) return
      this.createGroupLoading = true
      try {
        // Extract actual imsi_ids from selected items (selectedItems contains objects with return-object)
        let imsiIds
        if (this.selectAllMode) {
          imsiIds = this.allFilteredImsiIds
        } else {
          imsiIds = this.selectedItems
            .filter(item => item && item.imsi_id)
            .map(item => item.imsi_id)
        }
        const response = await createImsiGroup(this.token, this.newGroupName, this.newGroupDescription, imsiIds, this.newGroupClientId)
        if (!response.error) {
          this.showSnackbarMessage(`Group "${this.newGroupName}" created with ${response.group?.member_count || 0} members`, 'success')
          this.showCreateGroupDialog = false
          this.clearSelection()
        } else {
          this.showSnackbarMessage(response.message || 'Error creating group', 'error')
        }
      } catch (err) {
        log("Error creating group:", err)
        this.showSnackbarMessage('Error creating group', 'error')
      } finally {
        this.createGroupLoading = false
      }
    },

    async openAddToGroupDialog() {
      this.selectedGroupId = null
      this.showInlineCreateGroup = false
      this.inlineGroupName = ''
      this.inlineGroupDescription = ''
      this.inlineGroupClientId = this.client_id // Default to current user's client
      this.inlineGroupError = ''
      this.showAddToGroupDialog = true
      this.loadingExistingGroups = true
      // Load existing groups
      try {
        const response = await getImsiGroups(this.token)
        if (!response.error) {
          this.existingGroups = response.groups || []
        }
      } catch (err) {
        log("Error loading groups:", err)
      } finally {
        this.loadingExistingGroups = false
      }
    },

    closeAddToGroupDialog() {
      this.showAddToGroupDialog = false
      this.showInlineCreateGroup = false
    },

    cancelInlineCreateGroup() {
      this.showInlineCreateGroup = false
      this.inlineGroupName = ''
      this.inlineGroupDescription = ''
      this.inlineGroupError = ''
    },

    async createGroupInline() {
      if (!this.inlineGroupName) {
        this.inlineGroupError = 'Group name is required'
        return
      }
      this.inlineCreateGroupLoading = true
      this.inlineGroupError = ''
      try {
        // Create an empty group first (we'll add IMSIs after selecting it)
        const response = await createImsiGroup(
          this.token,
          this.inlineGroupName,
          this.inlineGroupDescription,
          [] // Empty - we'll add IMSIs via the normal "Add to Group" flow
        )
        if (!response.error && response.group) {
          // Add the new group to the list and select it
          this.existingGroups.unshift(response.group)
          this.selectedGroupId = response.group.id
          // Go back to the select view with the new group selected
          this.showInlineCreateGroup = false
          this.inlineGroupName = ''
          this.inlineGroupDescription = ''
          this.showSnackbarMessage(`Group "${response.group.name}" created. Click "Add to Group" to add the selected IMSIs.`, 'success')
        } else {
          this.inlineGroupError = response.message || 'Error creating group'
        }
      } catch (err) {
        log("Error creating group inline:", err)
        this.inlineGroupError = 'Error creating group'
      } finally {
        this.inlineCreateGroupLoading = false
      }
    },

    async addToGroup() {
      if (!this.selectedGroupId) return
      this.addToGroupLoading = true
      try {
        // Extract actual imsi_ids from selected items (selectedItems contains objects with return-object)
        let imsiIds
        if (this.selectAllMode) {
          imsiIds = this.allFilteredImsiIds
        } else {
          imsiIds = this.selectedItems
            .filter(item => item && item.imsi_id)
            .map(item => item.imsi_id)
        }
        const response = await addImsisToGroup(this.token, this.selectedGroupId, imsiIds)
        if (!response.error) {
          this.showSnackbarMessage(response.message || 'Added to group', 'success')
          this.showAddToGroupDialog = false
          this.showInlineCreateGroup = false
          this.clearSelection()
        } else {
          this.showSnackbarMessage(response.message || 'Error adding to group', 'error')
        }
      } catch (err) {
        log("Error adding to group:", err)
        this.showSnackbarMessage('Error adding to group', 'error')
      } finally {
        this.addToGroupLoading = false
      }
    },

    // ==================== Bulk Action Methods ====================

    confirmBulkAction(actionType) {
      this.bulkActionType = actionType
      this.showBulkActionDialog = true
    },

    async executeBulkAction() {
      // Get sim_ids for bulk operations (these operate on SIMs, not IMSIs)
      // selectedItems contains objects with return-object prop
      let simIds
      if (this.selectAllMode) {
        simIds = this.allFilteredSimIds
      } else {
        // Extract sim_ids from selected items (objects)
        simIds = this.selectedItems
          .filter(item => item && item.sim_id)  // Only include allocated IMSIs
          .map(item => item.sim_id)
      }

      if (simIds.length === 0) {
        this.showSnackbarMessage('No allocated SIM cards selected for this operation', 'warning')
        this.showBulkActionDialog = false
        return
      }

      this.bulkActionLoading = true

      try {
        let response
        switch (this.bulkActionType) {
          case 'suspend':
            response = await bulkSuspendSims(this.token, simIds)
            break
          case 'reactivate':
            response = await bulkReactivateSims(this.token, simIds)
            break
          case 'terminate':
            response = await bulkTerminateSims(this.token, simIds)
            break
          case 'provision':
            response = await bulkProvisionSims(this.token, simIds)
            break
          default:
            response = { error: true, message: 'Unknown action' }
        }

        if (!response.error) {
          this.showSnackbarMessage(response.message || `Bulk ${this.bulkActionType} completed`, 'success')
          this.showBulkActionDialog = false
          this.clearSelection()
          this.loadImsis() // Refresh list
        } else {
          this.showSnackbarMessage(response.message || `Error in bulk ${this.bulkActionType}`, 'error')
        }
      } catch (err) {
        log("Error in bulk action:", err)
        this.showSnackbarMessage(`Error in bulk ${this.bulkActionType}`, 'error')
      } finally {
        this.bulkActionLoading = false
      }
    },

    openBulkMoveDialog() {
      this.selectedTargetClient = null
      this.showBulkMoveDialog = true
    },

    async executeBulkMove() {
      if (!this.selectedTargetClient) return

      // Get imsi_ids for bulk move (works with both allocated and non-allocated IMSIs)
      // selectedItems contains objects with return-object prop
      let imsiIds
      if (this.selectAllMode) {
        imsiIds = this.allFilteredImsiIds
      } else {
        imsiIds = this.selectedItems
          .filter(item => item && item.imsi_id)
          .map(item => item.imsi_id)
      }

      if (imsiIds.length === 0) {
        this.showSnackbarMessage('No IMSIs selected for this operation', 'warning')
        this.showBulkMoveDialog = false
        return
      }

      this.bulkMoveLoading = true

      try {
        const response = await bulkMoveImsisToSubclient(this.token, imsiIds, this.selectedTargetClient)
        if (!response.error) {
          this.showSnackbarMessage(response.message || 'Bulk move completed', 'success')
          this.showBulkMoveDialog = false
          this.clearSelection()
          this.loadImsis() // Refresh list
        } else {
          this.showSnackbarMessage(response.message || 'Error in bulk move', 'error')
        }
      } catch (err) {
        log("Error in bulk move:", err)
        this.showSnackbarMessage('Error in bulk move', 'error')
      } finally {
        this.bulkMoveLoading = false
      }
    },

    async rebuildCache() {
      this.cacheRebuilding = true
      try {
        const { rebuildHierarchyCache } = await import('@/composables/useHierarchyCacheAPI')
        const result = await rebuildHierarchyCache(this.token)
        if (!result.error) {
          this.showSnackbarMessage('Cache rebuild started successfully', 'success')
        } else {
          this.showSnackbarMessage(result.message || 'Error rebuilding cache', 'error')
        }
      } catch (e) {
        this.showSnackbarMessage('Error rebuilding cache', 'error')
      } finally {
        this.cacheRebuilding = false
      }
    },

    exportCSV() {
      if (this.imsisList.length === 0) return

      const headers = ['Serial', 'IMSI', 'Status', 'MSISDN', 'Client', 'Expiration Date']
      const rows = this.imsisList.map(item => [
        item.serial || '',
        item.imsi || '',
        item.sim_status || '',
        item.msisdn || '',
        item.client_name || '',
        item.expiration_date || ''
      ])

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n')

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', `sims_export_${new Date().toISOString().split('T')[0]}.csv`)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  },

  created() {
    const {getLoggedIn, user, loggedIn, token} = useGetLoggedIn()
    getLoggedIn().then(async () => {
      log("ImsisList after getLoggedIn")
      log("user:")
      log(user)
      log("loggedIn: " + loggedIn.value)
      log("token: " + token.value)
      this.token = store.getters.getToken
      this.client_id = user.value?.client_id

      // Check user roles for MSISDN selection permission
      const userRoles = user.value?.roles || []
      this.hasSelectMsisdnRole = user.value?.admin || userRoles.includes('select_msisdn')

      // Check if current user is a distributor / multilevel reseller
      const clientTypeName = user.value?.client_type_name?.toLowerCase() || ''
      this.isDistributor = clientTypeName === 'distributor'
      this.isMultilevelReseller = clientTypeName === 'multilevel'

      // Load display settings for perPage
      let ds = store.state.displaySettings
      if (!ds || !ds.default_page_size_imsis) {
        const dsResult = await getDisplaySettings(this.token)
        if (!dsResult.error && dsResult.display_settings) {
          ds = dsResult.display_settings
          store.commit('setDisplaySettings', ds)
        }
      }
      if (ds && ds.default_page_size_imsis) {
        this.perPage = ds.default_page_size_imsis
      }

      // Check for search query parameter
      const searchParam = this.$route.query.search
      if (searchParam) {
        this.filters.search_term = searchParam
      }

      // Only load IMSIs if a search param was provided (don't load all on page open)
      if (searchParam && searchParam.length >= 3) {
        this.loadImsis()
      }
    })
  }
});
</script>

<style scoped>
.gap-1 {
  gap: 4px;
}
.gap-2 {
  gap: 8px;
}
.clickable-cell {
  cursor: pointer;
  text-decoration: underline;
  text-decoration-style: dotted;
}
.clickable-cell:hover {
  color: #1976d2;
  text-decoration-style: solid;
}
.selection-toolbar {
  position: sticky;
  top: 70px;
  z-index: 100;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
}
</style>

<style>
/* Row styles for data table - not scoped to apply to Vuetify components */
.v-data-table .row-suspended {
  background-color: rgba(255, 152, 0, 0.10) !important;
}

.v-data-table .row-suspended td {
  color: inherit !important;
}

.v-data-table .row-not_provisioned {
  background-color: rgba(33, 150, 243, 0.08) !important;
}
</style>
